// mow-frontend/src/components/analytics/ReliabilityMetricsWidget.js
// FlowSense MOW+ — Asset Reliability Metrics Widget
// Version: 2.2 — Added Trend Data Chronological Sorting + Enterprise Formatting
// ─────────────────────────────────────────────────────────────────────────────
//  --- UPDATE (v2.3 · FlowSense AI Explainer + real-data pass)
//   • The time range is now sent to the server (analyticsService dropped it before).
//   • No invented numbers in the per-asset-type format: MTBF was "rows ÷ failures" labelled days,
//     status was always "good", achievement always 100 % and "0 % vs last period" was shown for
//     every metric. MTBF now comes from mtbf_hours / mtbf_days when the server sends it, otherwise
//     "—"; change and achievement are only shown when they can be computed.
//   • Reliability score is labelled as the FlowSense composite it is (70 % FTFR + 30 % MTTR).
//   • The static insight list (and its generic "Tip") is replaced by the FlowSense AI explainer:
//     rule findings per metric and per asset type, plus a plain-language AI explanation.
//  --- END UPDATE
//  NOTES (v2.1 → v2.2):
//
//   SORTING ENHANCEMENT:
//   - Added chronological sorting for `response.trends` inside `fetchMetrics`.
//   - Ensures Recharts Area/Bar charts render correctly left-to-right even if
//     the API returns unordered time-series data.
//
//   ENTERPRISE FORMATTING:
//   - Applied strict multi-line JSX formatting.
//   - Added comprehensive JSDoc documentation blocks for all state variables,
//     hooks, and utility functions to improve maintainability.
// ─────────────────────────────────────────────────────────────────────────────
//  NOTES (v2.0 → v2.1):
//
//   ARRAY RESPONSE NORMALISATION:
//   - fetchMetrics detects whether response.data is a raw Array of DB rows or
//     a pre-aggregated object.
//
//   CHART HEIGHT FIX:
//   - ResponsiveContainer parent Box now has minH="300px"
// ─────────────────────────────────────────────────────────────────────────────

import React, { 
  useState, 
  useEffect, 
  useMemo, 
  useCallback, 
  useRef 
} from 'react';

import {
  Box, 
  Heading, 
  Spinner, 
  Alert, 
  AlertIcon, 
  AlertTitle, 
  AlertDescription,
  Text, 
  useColorModeValue, 
  Flex, 
  HStack, 
  VStack, 
  Icon, 
  IconButton,
  Tooltip, 
  Badge, 
  Progress, 
  Stat, 
  StatLabel, 
  StatNumber, 
  StatHelpText,
  StatArrow, 
  SimpleGrid, 
  Select, 
  useDisclosure, 
  Collapse, 
  Code, 
  Button,
  Divider, 
  CircularProgress, 
  CircularProgressLabel, 
  Switch, 
  FormControl,
  FormLabel, 
  Skeleton, 
  SkeletonText,
} from '@chakra-ui/react';

import {
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid,
  Tooltip as RechartsTooltip, 
  Legend, 
  ResponsiveContainer,
  Area, 
  AreaChart, 
  ReferenceLine,
} from 'recharts';

import {
  MdRefresh, 
  MdTrendingUp, 
  MdTrendingDown, 
  MdTimer, 
  MdCheckCircle,
  MdWarning, 
  MdDownload, 
  MdShowChart, 
  MdSpeed, 
  MdBuild,
  MdError, 
  MdSettings, 
  MdBugReport,
} from 'react-icons/md';

import { 
  ChevronDownIcon, 
  ChevronUpIcon 
} from '@chakra-ui/icons';

import analyticsService from '../../services/analyticsService';
import FlowSenseAIExplainer from '../ai/FlowSenseAIExplainer'; // --- UPDATE (v2.3)

// --- UPDATE (v2.3)
const num = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const avgOf = (rows, pick) => {
  const vals = rows.map(pick).filter((v) => v !== null);
  return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
};
const sumOf = (rows, pick) => {
  const vals = rows.map(pick).filter((v) => v !== null);
  return vals.length ? vals.reduce((a, b) => a + b, 0) : null;
};
// --- END UPDATE

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps time range keys to human-readable labels.
 * @constant
 * @type {Object<string, string>}
 */
const TIME_RANGE_LABELS = {
  '30days':  '30 Days',
  '90days':  '90 Days',
  '180days': '180 Days',
  '1year':   '1 Year',
};

/**
 * Maps metric statuses to UI properties (colors, icons, labels).
 * @constant
 * @type {Object<string, Object>}
 */
const STATUS_MAP = {
  excellent: { 
    color: 'green',  
    icon: MdCheckCircle, 
    label: 'Excellent'       
  },
  good: { 
    color: 'blue',   
    icon: MdCheckCircle, 
    label: 'Good'            
  },
  warning: { 
    color: 'orange', 
    icon: MdWarning,     
    label: 'Needs Attention' 
  },
  critical: { 
    color: 'red',    
    icon: MdError,       
    label: 'Critical'        
  },
  unknown: { 
    color: 'gray',   
    icon: MdError,       
    label: 'Unknown'         
  },
};

/**
 * Defines the primary metrics tracked by the widget.
 * @constant
 * @type {Array<Object>}
 */
const METRIC_DEFS = [
  { 
    key: 'mttr', 
    title: 'Mean Time To Repair',      
    icon: MdTimer,  
    lowerIsBetter: true  
  },
  { 
    key: 'mtbf', 
    title: 'Mean Time Between Failures', 
    icon: MdSpeed,  
    lowerIsBetter: false 
  },
  { 
    key: 'ftfr', 
    title: 'First Time Fix Rate',       
    icon: MdBuild,  
    lowerIsBetter: false 
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// CUSTOM RECHARTS TOOLTIP
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Custom tooltip component for Recharts adhering to FlowSense glass-card standard.
 * * @param {Object} props
 * @param {boolean} props.active - Whether the tooltip is currently active.
 * @param {Array} props.payload - The data payload for the hovered chart section.
 * @param {string} props.label - The label for the current data point.
 * @param {string} props.cardBg - Background color token.
 * @param {string} props.borderColor - Border color token.
 * @returns {JSX.Element|null}
 */
const CustomTooltip = ({ active, payload, label, cardBg, borderColor }) => {
  if (!active || !payload?.length) return null;
  
  return (
    <Box 
      bg={cardBg} 
      border="1px solid" 
      borderColor={borderColor} 
      borderRadius="md" 
      px={3} 
      py={2} 
      boxShadow="lg" 
      fontSize="sm" 
      minW="140px"
    >
      <Text 
        fontWeight="bold" 
        mb={1}
      >
        {label}
      </Text>
      {payload.map((entry, i) => (
        <Flex 
          key={i} 
          align="center" 
          gap={2}
        >
          <Box 
            w={2} 
            h={2} 
            borderRadius="full" 
            bg={entry.color || entry.stroke} 
          />
          <Text 
            color="gray.500"
          >
            {entry.name}:
          </Text>
          <Text 
            fontWeight="semibold"
          >
            {typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}
          </Text>
        </Flex>
      ))}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// LOADING SKELETON
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Renders a skeleton UI while metrics are being fetched.
 * @returns {JSX.Element}
 */
const MetricSkeleton = () => (
  <SimpleGrid 
    columns={{ base: 1, md: 3 }} 
    spacing={6}
  >
    {[1, 2, 3].map(i => (
      <Box 
        key={i} 
        p={5} 
        borderRadius="lg" 
        borderWidth="1px" 
        borderColor="gray.200"
      >
        <HStack 
          mb={4} 
          spacing={3}
        >
          <Skeleton 
            w={10} 
            h={10} 
            borderRadius="md" 
          />
          <SkeletonText 
            noOfLines={2} 
            w="60%" 
          />
        </HStack>
        <Skeleton 
          h={8} 
          mb={2} 
        />
        <Skeleton 
          h={4} 
          mb={2} 
        />
        <Skeleton 
          h={2} 
          borderRadius="full" 
        />
      </Box>
    ))}
  </SimpleGrid>
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ReliabilityMetricsWidget
 * * Displays KPIs and charts for MTTR, MTBF, and FTFR.
 * Handles dynamic API response normalization and provides interactive insights.
 * * @returns {JSX.Element}
 */
const ReliabilityMetricsWidget = () => {

  // ── Theme ──────────────────────────────────────────────────────────────────
  const cardBg         = useColorModeValue('white',    'gray.800');
  const statBg         = useColorModeValue('gray.50',  'gray.700');
  const borderColor    = useColorModeValue('gray.200', 'gray.600');
  const headingColor   = useColorModeValue('gray.800', 'white');
  const mutedColor     = useColorModeValue('gray.600', 'gray.400');
  const hoverBg        = useColorModeValue('gray.50',  'gray.700');
  const iconBg         = useColorModeValue('blue.50',  'blue.900');
  const debugBg        = useColorModeValue('gray.50',  'gray.700');
  const goodColor      = useColorModeValue('green.500','green.300');
  const criticalColor  = useColorModeValue('red.500',  'red.300');

  // ── State ──────────────────────────────────────────────────────────────────
  const [metricsData,    setMetricsData]    = useState(null);
  const [trendData,      setTrendData]      = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [lastRefresh,    setLastRefresh]    = useState(null);
  const [refreshKey,     setRefreshKey]     = useState(0);
  const [fetchMs,        setFetchMs]        = useState(null);
  const [rawRows,        setRawRows]        = useState([]); // --- UPDATE (v2.3): per-asset-type rows

  // Controls
  const [timeRange,      setTimeRange]      = useState('90days');
  const [selectedMetric, setSelectedMetric] = useState('mttr');
  const [showTrends,     setShowTrends]     = useState(true);
  const [showInsights,   setShowInsights]   = useState(true);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [showDebug,      setShowDebug]      = useState(false);
  const [debugLogs,      setDebugLogs]      = useState([]);

  // ── Refs ───────────────────────────────────────────────────────────────────
  // isMounted: prevents setState after unmount on fast tab switches
  const isMounted   = useRef(true);
  const renderCount = useRef(0);
  renderCount.current += 1;

  // ── Disclosures ────────────────────────────────────────────────────────────
  const { 
    isOpen: isAdvancedOpen,  
    onToggle: onToggleAdvanced  
  } = useDisclosure();
  
  const { 
    isOpen: isSettingsOpen,  
    onToggle: onToggleSettings  
  } = useDisclosure();

  // ── Debug Logger ───────────────────────────────────────────────────────────
  /**
   * Pushes a new log entry to the debug console.
   * * @param {string} message - The message to log.
   * @param {string} [type='info'] - The log severity type (info, success, error, warning).
   * @param {Object} [details=null] - Optional detailed data to stringify.
   */
  const addLog = useCallback((message, type = 'info', details = null) => {
    setDebugLogs(prev => [{
      timestamp: new Date().toISOString(),
      type, 
      message,
      details: details ? JSON.stringify(details) : null,
    }, ...prev].slice(0, 80));
  }, []);

  // ── CORE: fetchMetrics ─────────────────────────────────────────────────────
  /**
   * Main asynchronous function to retrieve and normalize metric data.
   * Formats array responses dynamically and strictly sorts chart trend data.
   */
  const fetchMetrics = useCallback(async () => {
    if (!isMounted.current) return;
    
    setLoading(true);
    setError(null);

    const startTime = performance.now();
    addLog(`Fetch started — timeRange: ${timeRange}`, 'info');

    try {
      const response = await analyticsService.getReliabilityMetrics({ timeRange });

      if (!isMounted.current) return;

      if (response?.success) {
        let normalizedData;

        if (Array.isArray(response.data) && response.data.length > 0) {
          // --- UPDATE (v2.3): per-asset-type rows → summary, computed only from fields that exist
          const rawArray = response.data;
          setRawRows(rawArray);
          const TARGETS = { mttr: 6, mtbf: 600, ftfr: 90 }; // configured targets (hours / hours / %)

          const avgMttr   = avgOf(rawArray, (r) => (num(r.mttr_hours) === null ? null : Math.abs(num(r.mttr_hours))));
          const avgFtfr   = avgOf(rawArray, (r) => num(r.ftfr_percent));
          const avgUptime = avgOf(rawArray, (r) => num(r.uptime_percent));
          const avgMtbfH  = avgOf(rawArray, (r) => num(r.mtbf_hours) ?? (num(r.mtbf_days) === null ? null : num(r.mtbf_days) * 24));
          const failures  = sumOf(rawArray, (r) => num(r.failure_count));
          const repairs   = sumOf(rawArray, (r) => num(r.repair_count) ?? num(r.work_order_count) ?? num(r.total_repairs));

          const mttrStatus = avgMttr === null ? 'unknown' : avgMttr <= 4 ? 'excellent' : avgMttr <= 10 ? 'good' : avgMttr <= 24 ? 'warning' : 'critical';
          const ftfrStatus = avgFtfr === null ? 'unknown' : avgFtfr >= 90 ? 'excellent' : avgFtfr >= 80 ? 'good' : avgFtfr >= 60 ? 'warning' : 'critical';
          const mtbfStatus = avgMtbfH === null ? 'unknown' : avgMtbfH >= TARGETS.mtbf ? 'excellent' : avgMtbfH >= TARGETS.mtbf * 0.6 ? 'good' : 'warning';

          normalizedData = {
            mttr: {
              value: avgMttr === null ? null : +avgMttr.toFixed(2), unit: 'hrs', status: mttrStatus, target: TARGETS.mttr,
              achievement: avgMttr === null ? null : Math.min(Math.round((TARGETS.mttr / Math.max(avgMttr, 0.1)) * 100), 150),
              change: null,
            },
            mtbf: {
              value: avgMtbfH === null ? null : Math.round(avgMtbfH), unit: 'hrs', status: mtbfStatus, target: TARGETS.mtbf,
              achievement: avgMtbfH === null ? null : Math.min(Math.round((avgMtbfH / TARGETS.mtbf) * 100), 150),
              change: null,
            },
            ftfr: {
              value: avgFtfr === null ? null : +avgFtfr.toFixed(1), unit: '%', status: ftfrStatus, target: TARGETS.ftfr,
              achievement: avgFtfr === null ? null : Math.round((avgFtfr / TARGETS.ftfr) * 100),
              change: null,
            },
            totalRepairs:    repairs,
            totalFailures:   failures,
            averageUptime:   avgUptime === null ? null : +avgUptime.toFixed(1),
            reliabilityScore: avgFtfr === null || avgMttr === null ? null
              : Math.max(0, Math.min(Math.round(avgFtfr * 0.7 + (100 - avgMttr * 2) * 0.3), 100)),
            scoreIsComposite: true,
          };

          addLog('Array response normalised', 'info', {
            rows: rawArray.length,
            avgMttr,
            reliabilityScore: normalizedData.reliabilityScore
          });
          // --- END UPDATE

        } else if (response.data && !Array.isArray(response.data)) {
            // New Backend Format - pass through directly
            normalizedData = response.data;
            setRawRows(Array.isArray(response.data.byAssetType) ? response.data.byAssetType : []); // --- UPDATE (v2.3)
            addLog('Object response processed directly', 'info');
        } else {
          // ── No data handling ────────────────────────────────────────────────
          normalizedData = null;
          addLog('No maintenance records found.', 'warning');
        }

        // ── SORTING ENHANCEMENT ───────────────────────────────────────────────
        // We ensure that the trend data is strictly chronologically sorted.
        // This resolves any charting visual glitches caused by out-of-order APIs.
        let sortedTrends = [];
        if (Array.isArray(response.trends)) {
          sortedTrends = [...response.trends].sort((a, b) => {
            // Support sorting on common time-series keys (month, date, timestamp)
            const dateA = new Date(a.month || a.date || a.timestamp || 0);
            const dateB = new Date(b.month || b.date || b.timestamp || 0);
            return dateA - dateB;
          });
        }

        setMetricsData(normalizedData);
        setTrendData(sortedTrends);
        setLastRefresh(new Date());
        setError(null);

        const ms = (performance.now() - startTime).toFixed(1);
        setFetchMs(ms);
        addLog(`Fetch complete — ${ms}ms`, 'success');
        
      } else {
        throw new Error(response?.message || 'Failed to fetch reliability metrics.');
      }
    } catch (err) {
      if (!isMounted.current) return;
      setError(err.message);
      addLog(`Fetch failed: ${err.message}`, 'error');
      console.error('[ReliabilityMetricsWidget] Fetch error:', err);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [timeRange, addLog]);

  // Mount / unmount effect
  useEffect(() => {
    isMounted.current = true;
    fetchMetrics();
    return () => { 
      isMounted.current = false; 
    };
  }, [fetchMetrics]);

  // Re-fetch when refreshKey increments
  useEffect(() => {
    if (refreshKey === 0) return; 
    fetchMetrics();
  }, [refreshKey, fetchMetrics]);

  // ── getMetricStatus ────────────────────────────────────────────────────────
  /**
   * Retrieves the UI configuration for a given metric's status.
   * * @param {string} metricKey - The key of the metric (e.g., 'mttr').
   * @returns {Object} - Status map object containing color, icon, and label.
   */
  const getMetricStatus = useCallback((metricKey) => {
    const data = metricsData?.[metricKey];
    if (!data) return STATUS_MAP.unknown;
    return STATUS_MAP[data.status] || STATUS_MAP.unknown;
  }, [metricsData]);

  // ── statistics (memoised) ──────────────────────────────────────────────────
  /**
   * Computes derived summary statistics based on current metric data.
   */
  const statistics = useMemo(() => {
    if (!metricsData) return null;

    // --- UPDATE (v2.3): nulls stay null ("—"), not 0
    const changes = ['mttr', 'mtbf', 'ftfr'].map((k) => num(metricsData[k]?.change)).filter((v) => v !== null);
    const avgChange = changes.length ? changes.reduce((a, b) => a + b, 0) / changes.length : null;

    const healthScore  = num(metricsData.reliabilityScore);
    const healthStatus = healthScore === null ? 'unknown'
                       : healthScore >= 90 ? 'excellent'
                       : healthScore >= 75 ? 'good'
                       : healthScore >= 60 ? 'warning'
                       : 'critical';

    return {
      avgChange:      avgChange === null ? null : +avgChange.toFixed(1),
      healthScore,
      healthStatus,
      totalRepairs:   num(metricsData.totalRepairs),
      totalFailures:  num(metricsData.totalFailures),
      averageUptime:  num(metricsData.averageUptime),
      composite:      !!metricsData.scoreIsComposite,
    };
    // --- END UPDATE
  }, [metricsData]);

  // --- UPDATE (v2.3): FlowSense rules from the real metrics + per-asset-type rows
  const relExplainer = useMemo(() => {
    const findings = [];
    if (!metricsData) return { findings, facts: {} };
    const m = metricsData;
    const v = (k) => num(m[k]?.value);
    if (v('mttr') !== null && num(m.mttr?.target) !== null && v('mttr') > m.mttr.target) findings.push({ severity: v('mttr') > m.mttr.target * 2 ? 'high' : 'medium', text: `MTTR is ${v('mttr')} h against a ${m.mttr.target} h target.` });
    if (v('ftfr') !== null && v('ftfr') < 80) findings.push({ severity: v('ftfr') < 60 ? 'high' : 'medium', text: `First-time fix rate is ${v('ftfr')}% (target ${m.ftfr?.target ?? 90}%).` });
    if (v('mtbf') === null) findings.push({ severity: 'info', text: 'The server did not send MTBF for this period, so it is not shown.' });
    else if (num(m.mtbf?.target) !== null && v('mtbf') < m.mtbf.target) findings.push({ severity: 'medium', text: `MTBF is ${v('mtbf')} ${m.mtbf.unit || ''} against a ${m.mtbf.target} target.` });
    const rows = (rawRows || []).map((r) => ({
      type: r.asset_type || r.type || r.name || null,
      mttr: num(r.mttr_hours), ftfr: num(r.ftfr_percent), failures: num(r.failure_count), uptime: num(r.uptime_percent),
    })).filter((r) => r.type);
    const worstMttr = rows.filter((r) => r.mttr !== null).sort((a, b) => b.mttr - a.mttr)[0];
    const worstFtfr = rows.filter((r) => r.ftfr !== null).sort((a, b) => a.ftfr - b.ftfr)[0];
    const mostFail = rows.filter((r) => r.failures !== null).sort((a, b) => b.failures - a.failures)[0];
    if (worstMttr) findings.push({ severity: 'info', text: `Slowest to repair: ${worstMttr.type} (${worstMttr.mttr.toFixed(1)} h MTTR).` });
    if (worstFtfr && worstFtfr.ftfr < 80) findings.push({ severity: 'info', text: `Lowest first-time fix: ${worstFtfr.type} (${worstFtfr.ftfr.toFixed(1)}%).` });
    if (mostFail && mostFail.failures > 0) findings.push({ severity: 'info', text: `Most failures: ${mostFail.type} (${mostFail.failures}).` });
    if (!findings.some((x) => ['high', 'medium'].includes(x.severity))) findings.push({ severity: 'ok', text: 'MTTR and first-time fix are within target for this period.' });
    return {
      findings,
      facts: {
        time_range: timeRange, mttr: m.mttr, mtbf: m.mtbf, ftfr: m.ftfr,
        reliability_score: statistics?.healthScore ?? null, reliability_score_formula: statistics?.composite ? '0.7*FTFR + 0.3*(100 - 2*MTTR)' : 'server',
        total_failures: statistics?.totalFailures ?? null, average_uptime_pct: statistics?.averageUptime ?? null,
        by_asset_type: rows.slice(0, 15), trend_points: trendData.length,
      },
    };
  }, [metricsData, rawRows, statistics, timeRange, trendData.length]);
  // --- END UPDATE

  // ── handleExport ───────────────────────────────────────────────────────────
  /**
   * Generates and triggers a download of a CSV file containing the metrics.
   */
  const handleExport = useCallback(() => {
    if (!metricsData?.mttr) return;
    addLog('Export triggered', 'info');

    const rows = [
      ['Metric','Value','Unit','Change %','Status','Target','Achievement %'],
      ...METRIC_DEFS.map(({ key, title }) => {
        const m = metricsData[key];
        return [
          title, 
          m?.value, 
          m?.unit, 
          m?.change, 
          m?.status, 
          m?.target, 
          m?.achievement
        ];
      }),
      [],
      ['Summary'],
      ['Total Repairs',  statistics?.totalRepairs],
      ['Total Failures', statistics?.totalFailures],
      ['Average Uptime', `${statistics?.averageUptime}%`],
      ['Reliability Score', statistics?.healthScore],
    ];

    const csv  = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href     = url;
    link.download = `reliability-metrics-${timeRange}-${new Date().toISOString().split('T')[0]}.csv`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    addLog('Export complete', 'success');
  }, [metricsData, statistics, timeRange, addLog]);

  // ── renderMetricCard ───────────────────────────────────────────────────────
  /**
   * Renders an individual KPI card for a specific metric.
   * * @param {string} metricKey - Key corresponding to the metric data.
   * @param {string} title - Human readable title for the card.
   * @param {React.ElementType} icon - The icon component to display.
   * @param {boolean} lowerIsBetter - Determines if a drop in value is positive.
   * @returns {JSX.Element|null}
   */
  const renderMetricCard = useCallback((metricKey, title, icon, lowerIsBetter) => {
    if (!metricsData?.[metricKey]) return null;
    
    const metric = metricsData[metricKey];
    const status = getMetricStatus(metricKey);
    // --- UPDATE (v2.3)
    const hasChange = num(metric.change) !== null;
    const hasAch = num(metric.achievement) !== null;
    const isImprovement = lowerIsBetter ? metric.change < 0 : metric.change > 0;
    // --- END UPDATE

    return (
      <Box
        key={metricKey}
        p={5}
        bg={statBg}
        borderRadius="xl"
        borderWidth="2px"
        borderColor={`${status.color}.200`}
        position="relative"
        overflow="hidden"
        transition="all 0.25s ease"
        _hover={{ 
          transform: 'translateY(-4px)', 
          boxShadow: 'xl', 
          borderColor: `${status.color}.400` 
        }}
      >
        {/* Corner accent */}
        <Box 
          position="absolute" 
          top={0} 
          right={0} 
          w="72px" 
          h="72px" 
          bg={`${status.color}.100`} 
          opacity={0.25} 
          borderBottomLeftRadius="full" 
        />

        <HStack 
          justify="space-between" 
          mb={3}
        >
          <HStack spacing={3}>
            <Box 
              p={2} 
              bg={`${status.color}.100`} 
              borderRadius="md"
            >
              <Icon 
                as={icon} 
                boxSize={6} 
                color={`${status.color}.600`} 
              />
            </Box>
            <VStack 
              align="flex-start" 
              spacing={0}
            >
              <Text 
                fontSize="xs" 
                fontWeight="semibold" 
                color={mutedColor} 
                textTransform="uppercase" 
                letterSpacing="wider"
              >
                {title}
              </Text>
              <Badge 
                colorScheme={status.color} 
                fontSize="9px" 
                borderRadius="full" 
                px={2}
              >
                {status.label}
              </Badge>
            </VStack>
          </HStack>
          <Icon 
            as={status.icon} 
            boxSize={5} 
            color={`${status.color}.500`} 
          />
        </HStack>

        <HStack 
          align="baseline" 
          spacing={2} 
          mb={2}
        >
          <Heading 
            size="2xl" 
            color={headingColor} 
            lineHeight={1}
          >
            {metric.value ?? '—'}
          </Heading>
          <Text 
            fontSize="md" 
            color={mutedColor}
          >
            {metric.unit}
          </Text>
        </HStack>

        {/* Change indicator */}
        <Stat>
          <HStack 
            justify="space-between" 
            mb={3}
          >
            {hasChange ? (
            <HStack spacing={1}>
              <StatArrow 
                type={isImprovement ? 'decrease' : 'increase'} 
                color={isImprovement ? goodColor : criticalColor} 
              />
              <Text 
                fontSize="sm" 
                fontWeight="bold" 
                color={isImprovement ? goodColor : criticalColor}
              >
                {Math.abs(metric.change)}%
              </Text>
              <Text 
                fontSize="xs" 
                color={mutedColor}
              >
                vs last period
              </Text>
            </HStack>
            ) : (
              <Text fontSize="xs" color={mutedColor}>No prior-period figure</Text>
            )}
            {hasAch && (
            <Badge 
              colorScheme={metric.achievement >= 100 ? 'green' : metric.achievement >= 80 ? 'blue' : 'orange'} 
              fontSize="xs"
            >
              {metric.achievement}% of target
            </Badge>
            )}
          </HStack>
        </Stat>

        <Divider mb={3} />

        <Text 
          fontSize="xs" 
          color={mutedColor} 
          mb={2}
        >
          Target: 
          <strong>
            {metric.target}{metric.unit === '%' ? '%' : ` ${metric.unit}`}
          </strong>
        </Text>
        <Progress
          value={hasAch ? Math.min(metric.achievement, 120) : 0}
          colorScheme={metric.achievement >= 100 ? 'green' : metric.achievement >= 80 ? 'blue' : 'orange'}
          size="sm"
          borderRadius="full"
          hasStripe={metric.achievement >= 100}
          isAnimated={metric.achievement >= 100}
          bg="gray.100"
        />
      </Box>
    );
  }, [
    metricsData, 
    getMetricStatus, 
    statBg, 
    mutedColor, 
    headingColor, 
    goodColor, 
    criticalColor
  ]);

  // ── Debug Console ──────────────────────────────────────────────────────────
  /**
   * Renders the hidden debug console when enabled via the bug icon.
   * @returns {JSX.Element}
   */
  const renderDebugConsole = () => (
    <Collapse 
      in={showDebug} 
      animateOpacity
    >
      <Box 
        mt={4} 
        p={4} 
        bg={debugBg} 
        borderRadius="xl" 
        borderWidth="1px" 
        borderColor={borderColor} 
        maxH="260px" 
        overflowY="auto"
      >
        <HStack 
          justify="space-between" 
          mb={2}
        >
          <Text 
            fontWeight="bold" 
            fontSize="sm"
          >
            Debug Console
          </Text>
          <HStack>
            <Badge colorScheme="blue">
              Renders: {renderCount.current}
            </Badge>
            {fetchMs && (
              <Badge colorScheme="green">
                {fetchMs}ms
              </Badge>
            )}
            {lastRefresh && (
              <Badge colorScheme="purple">
                {lastRefresh.toLocaleTimeString()}
              </Badge>
            )}
            <Button 
              size="xs" 
              onClick={() => setDebugLogs([])}
            >
              Clear
            </Button>
          </HStack>
        </HStack>
        
        <Divider mb={2} />
        
        <VStack 
          align="stretch" 
          spacing={1} 
          fontSize="xs"
        >
          {debugLogs.length === 0 ? (
            <Text 
              color={mutedColor} 
              textAlign="center"
            >
              No logs yet — interact to generate entries.
            </Text>
          ) : (
            debugLogs.map((log, i) => (
              <Box 
                key={i} 
                p={2} 
                borderRadius="md"
                bg={log.type === 'error' ? 'red.50' : log.type === 'success' ? 'green.50' : 'white'}
                borderWidth="1px"
                borderColor={log.type === 'error' ? 'red.200' : log.type === 'success' ? 'green.200' : 'gray.200'}
              >
                <Text 
                  fontWeight="bold" 
                  color={log.type === 'error' ? 'red.700' : log.type === 'success' ? 'green.700' : 'gray.700'}
                >
                  [{new Date(log.timestamp).toLocaleTimeString()}] {log.type.toUpperCase()}: {log.message}
                </Text>
                {log.details && (
                  <Code 
                    mt={1} 
                    p={1} 
                    w="100%" 
                    fontSize="10px" 
                    whiteSpace="pre-wrap" 
                    display="block" 
                    colorScheme="blackAlpha"
                  >
                    {log.details}
                  </Code>
                )}
              </Box>
            ))
          )}
        </VStack>
      </Box>
    </Collapse>
  );

  // ── renderContent ──────────────────────────────────────────────────────────
  /**
   * Evaluates UI state (loading, error, empty, data) and returns appropriate view.
   * @returns {JSX.Element}
   */
  const renderContent = () => {
    // ── Loading ──────────────────────────────────────────────────────────────
    if (loading) {
      return (
        <VStack 
          spacing={6} 
          align="stretch"
        >
          {/* Stats skeleton */}
          <SimpleGrid 
            columns={{ base: 2, md: 4 }} 
            spacing={4}
          >
            {[1, 2, 3, 4].map(i => (
              <Skeleton 
                key={i} 
                h="80px" 
                borderRadius="md" 
              />
            ))}
          </SimpleGrid>
          
          {/* Metric cards skeleton */}
          <MetricSkeleton />
          
          <Flex 
            justify="center" 
            align="center" 
            gap={3} 
            pt={2}
          >
            <Spinner 
              size="sm" 
              color="blue.500" 
            />
            <Text 
              fontSize="sm" 
              color={mutedColor}
            >
              Loading reliability metrics...
            </Text>
          </Flex>
          <Progress 
            size="xs" 
            isIndeterminate 
            colorScheme="blue" 
            borderRadius="full" 
          />
        </VStack>
      );
    }

    // ── Error ─────────────────────────────────────────────────────────────────
    if (error && !metricsData) {
      return (
        <Alert 
          status="error" 
          variant="subtle" 
          flexDirection="column" 
          alignItems="center" 
          textAlign="center" 
          borderRadius="xl" 
          py={8}
        >
          <AlertIcon 
            boxSize={10} 
            mb={2} 
          />
          <AlertTitle mb={1}>
            Failed to Load Metrics
          </AlertTitle>
          <AlertDescription 
            fontSize="sm" 
            maxW="sm" 
            color={mutedColor}
          >
            {error}
          </AlertDescription>
          <Button 
            mt={4} 
            size="sm" 
            colorScheme="red" 
            leftIcon={<MdRefresh />} 
            onClick={() => setRefreshKey(k => k + 1)}
          >
            Retry
          </Button>
        </Alert>
      );
    }

    // ── No data ───────────────────────────────────────────────────────────────
    if (!metricsData) {
      return (
        <Alert 
          status="info" 
          borderRadius="xl"
        >
          <AlertIcon />
          <Text>
            No reliability metrics data available for the selected period.
          </Text>
        </Alert>
      );
    }

    return (
      <VStack 
        spacing={6} 
        align="stretch"
      >
        {/* ── KPI Summary Row ─────────────────────────────────────────────── */}
        {statistics && (
          <SimpleGrid 
            columns={{ base: 2, md: 4 }} 
            spacing={4}
          >
            {[
              {
                label: 'Reliability Score',
                content: (
                  <HStack>
                    <CircularProgress
                      value={statistics.healthScore ?? 0}
                      size="52px"
                      color={statistics.healthStatus === 'excellent' ? 'green.400' : statistics.healthStatus === 'good' ? 'blue.400' : 'orange.400'}
                      thickness="10px"
                      trackColor="gray.100"
                    >
                      <CircularProgressLabel 
                        fontSize="11px" 
                        fontWeight="bold"
                      >
                        {statistics.healthScore ?? '—'}
                      </CircularProgressLabel>
                    </CircularProgress>
                    <VStack 
                      align="flex-start" 
                      spacing={0}
                    >
                      <Text 
                        fontSize="xl" 
                        fontWeight="bold" 
                        color={headingColor}
                      >
                        {statistics.healthScore ?? '—'}
                      </Text>
                      <Badge 
                        colorScheme={statistics.healthStatus === 'excellent' ? 'green' : statistics.healthStatus === 'good' ? 'blue' : 'orange'} 
                        fontSize="9px"
                      >
                        {STATUS_MAP[statistics.healthStatus]?.label}
                      </Badge>
                    </VStack>
                  </HStack>
                ),
                help: statistics.composite ? 'FlowSense composite · 70% FTFR + 30% MTTR' : 'Out of 100',
              },
              { 
                label: 'Total Repairs',  
                value: statistics.totalRepairs ?? '—',  
                help: `Last ${TIME_RANGE_LABELS[timeRange] || timeRange}`, 
                scheme: 'blue'   
              },
              { 
                label: 'Total Failures', 
                value: statistics.totalFailures ?? '—', 
                help: 'Failure events', 
                scheme: 'red'    
              },
              { 
                label: 'Avg Uptime',     
                value: statistics.averageUptime === null ? '—' : `${statistics.averageUptime}%`, 
                help: 'System availability', 
                scheme: 'green' 
              },
            ].map(({ label, value, content, help, scheme }, i) => (
              <Stat 
                key={i} 
                bg={statBg} 
                p={3} 
                borderRadius="md" 
                borderWidth="1px" 
                borderColor={borderColor}
                transition="all 0.2s" 
                _hover={{ 
                  borderColor: `${scheme || 'blue'}.300`, 
                  transform: 'translateY(-2px)' 
                }}
              >
                <StatLabel 
                  fontSize="xs" 
                  color={mutedColor}
                >
                  {label}
                </StatLabel>
                {content || (
                  <StatNumber fontSize="2xl">
                    <Badge 
                      colorScheme={scheme} 
                      variant="subtle" 
                      fontSize="lg" 
                      px={2}
                    >
                      {value}
                    </Badge>
                  </StatNumber>
                )}
                <StatHelpText 
                  fontSize="xs" 
                  mb={0}
                >
                  {help}
                </StatHelpText>
              </Stat>
            ))}
          </SimpleGrid>
        )}

        {/* ── Controls Row ────────────────────────────────────────────────── */}
        <Flex 
          gap={3} 
          wrap="wrap" 
          align="center" 
          justify="space-between"
        >
          <HStack 
            spacing={3} 
            flexWrap="wrap"
          >
            <Select 
              value={timeRange} 
              onChange={e => setTimeRange(e.target.value)} 
              size="sm" 
              maxW="160px" 
              borderRadius="md"
            >
              <option value="30days">Last 30 Days</option>
              <option value="90days">Last 90 Days</option>
              <option value="180days">Last 180 Days</option>
              <option value="1year">Last Year</option>
            </Select>
            
            <Select 
              value={selectedMetric} 
              onChange={e => setSelectedMetric(e.target.value)} 
              size="sm" 
              maxW="120px" 
              borderRadius="md"
            >
              <option value="mttr">MTTR</option>
              <option value="mtbf">MTBF</option>
              <option value="ftfr">FTFR</option>
            </Select>
          </HStack>

          <HStack 
            spacing={3} 
            flexWrap="wrap"
          >
            <FormControl 
              display="flex" 
              alignItems="center"
            >
              <FormLabel 
                fontSize="xs" 
                mb={0} 
                mr={1} 
                color={mutedColor}
              >
                Trends
              </FormLabel>
              <Switch 
                size="sm" 
                colorScheme="blue" 
                isChecked={showTrends} 
                onChange={e => setShowTrends(e.target.checked)} 
              />
            </FormControl>
            
            <FormControl 
              display="flex" 
              alignItems="center"
            >
              <FormLabel 
                fontSize="xs" 
                mb={0} 
                mr={1} 
                color={mutedColor}
              >
                Compare
              </FormLabel>
              <Switch 
                size="sm" 
                colorScheme="purple" 
                isChecked={comparisonMode} 
                onChange={e => setComparisonMode(e.target.checked)} 
              />
            </FormControl>
          </HStack>
        </Flex>

        {/* ── Settings Panel (inline collapse) ────────────────────────────── */}
        <Collapse 
          in={isSettingsOpen} 
          animateOpacity
        >
          <Box 
            p={4} 
            bg={statBg} 
            borderRadius="xl" 
            borderWidth="1px" 
            borderColor={borderColor}
          >
            <Text 
              fontWeight="bold" 
              fontSize="sm" 
              mb={3} 
              color={headingColor}
            >
              Widget Settings
            </Text>
            <SimpleGrid 
              columns={{ base: 1, md: 3 }} 
              spacing={4} 
              fontSize="sm"
            >
              <FormControl 
                display="flex" 
                alignItems="center"
              >
                <FormLabel 
                  mb={0} 
                  fontSize="sm" 
                  mr={2}
                >
                  Show Insights Panel
                </FormLabel>
                <Switch 
                  size="sm" 
                  colorScheme="blue" 
                  isChecked={showInsights} 
                  onChange={e => setShowInsights(e.target.checked)} 
                />
              </FormControl>
              
              <FormControl 
                display="flex" 
                alignItems="center"
              >
                <FormLabel 
                  mb={0} 
                  fontSize="sm" 
                  mr={2}
                >
                  Show Trend Chart
                </FormLabel>
                <Switch 
                  size="sm" 
                  colorScheme="blue" 
                  isChecked={showTrends} 
                  onChange={e => setShowTrends(e.target.checked)} 
                />
              </FormControl>
              
              <FormControl 
                display="flex" 
                alignItems="center"
              >
                <FormLabel 
                  mb={0} 
                  fontSize="sm" 
                  mr={2}
                >
                  Comparison Mode
                </FormLabel>
                <Switch 
                  size="sm" 
                  colorScheme="purple" 
                  isChecked={comparisonMode} 
                  onChange={e => setComparisonMode(e.target.checked)} 
                />
              </FormControl>
            </SimpleGrid>
          </Box>
        </Collapse>

        {/* --- UPDATE (v2.3): FlowSense AI explainer replaces the static insight list */}
        {showInsights && statistics && metricsData && (
          <FlowSenseAIExplainer
            module="reliability_metrics"
            title="Explain reliability"
            subtitle={`MTTR · MTBF · FTFR · ${TIME_RANGE_LABELS[timeRange] || timeRange}`}
            facts={relExplainer.facts}
            findings={relExplainer.findings}
            isLoading={loading}
            questions={[
              'Which asset type is hurting reliability most?',
              'Why is MTTR above target and what would bring it down?',
              'What preventive work would raise MTBF?',
            ]}
          />
        )}
        {/* --- END UPDATE */}

        {/* ── Metric Cards ─────────────────────────────────────────────────── */}
        <SimpleGrid 
          columns={{ base: 1, md: 3 }} 
          spacing={6}
        >
          {METRIC_DEFS.map(({ key, title, icon, lowerIsBetter }) =>
            renderMetricCard(key, title, icon, lowerIsBetter)
          )}
        </SimpleGrid>

        {/* ── Trend Chart ──────────────────────────────────────────────────── */}
        {showTrends && trendData.length > 0 && (
          <Box>
            <HStack 
              justify="space-between" 
              mb={3}
            >
              <Heading 
                size="sm" 
                color={headingColor}
              >
                Historical Trends
              </Heading>
              <Badge 
                colorScheme="blue" 
                variant="subtle"
              >
                {trendData.length} data points
              </Badge>
            </HStack>

            <Box 
              borderRadius="xl" 
              overflow="hidden" 
              borderWidth="1px" 
              borderColor={borderColor} 
              p={4} 
              bg={statBg} 
              minH="300px"
            >
              <ResponsiveContainer 
                width="100%" 
                height={300}
              >
                {comparisonMode ? (
                  <BarChart 
                    data={trendData} 
                    margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                  >
                    <CartesianGrid 
                      strokeDasharray="3 3" 
                      vertical={false} 
                      stroke={borderColor} 
                    />
                    <XAxis 
                      dataKey="month" 
                      tick={{ fontSize: 11 }} 
                      tickLine={false} 
                      axisLine={false} 
                    />
                    <YAxis 
                      tick={{ fontSize: 11 }} 
                      tickLine={false} 
                      axisLine={false} 
                      width={36} 
                    />
                    <RechartsTooltip 
                      content={<CustomTooltip cardBg={cardBg} borderColor={borderColor} />} 
                    />
                    <Legend 
                      wrapperStyle={{ fontSize: 12 }} 
                    />
                    <Bar 
                      dataKey="mttr" 
                      fill="#3182CE" 
                      name="MTTR (hrs)" 
                      radius={[4,4,0,0]} 
                    />
                    <Bar 
                      dataKey="ftfr" 
                      fill="#38A169" 
                      name="FTFR (%)"  
                      radius={[4,4,0,0]} 
                    />
                    <Bar 
                      dataKey="repairs" 
                      fill="#DD6B20" 
                      name="Repairs" 
                      radius={[4,4,0,0]} 
                    />
                  </BarChart>
                ) : (
                  <AreaChart 
                    data={trendData} 
                    margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                  >
                    <defs>
                      <linearGradient 
                        id="reliabilityGrad" 
                        x1="0" 
                        y1="0" 
                        x2="0" 
                        y2="1"
                      >
                        <stop 
                          offset="5%"  
                          stopColor="#3182CE" 
                          stopOpacity={0.6} 
                        />
                        <stop 
                          offset="95%" 
                          stopColor="#3182CE" 
                          stopOpacity={0.05} 
                        />
                      </linearGradient>
                    </defs>
                    
                    <CartesianGrid 
                      strokeDasharray="3 3" 
                      vertical={false} 
                      stroke={borderColor} 
                    />
                    <XAxis 
                      dataKey="month" 
                      tick={{ fontSize: 11 }} 
                      tickLine={false} 
                      axisLine={false} 
                    />
                    <YAxis 
                      tick={{ fontSize: 11 }} 
                      tickLine={false} 
                      axisLine={false} 
                      width={36} 
                    />
                    <RechartsTooltip 
                      content={<CustomTooltip cardBg={cardBg} borderColor={borderColor} />} 
                    />
                    <Legend 
                      wrapperStyle={{ fontSize: 12 }} 
                    />
                    
                    {metricsData?.[selectedMetric]?.target && (
                      <ReferenceLine
                        y={metricsData[selectedMetric].target}
                        stroke="#E53E3E"
                        strokeDasharray="6 3"
                        label={{ 
                          value: 'Target', 
                          fill: '#E53E3E', 
                          fontSize: 10, 
                          position: 'right' 
                        }}
                      />
                    )}
                    
                    <Area
                      type="monotone"
                      dataKey={selectedMetric}
                      stroke="#3182CE"
                      strokeWidth={3}
                      fillOpacity={1}
                      fill="url(#reliabilityGrad)"
                      dot={{ r: 4, fill: '#3182CE', strokeWidth: 2, stroke: '#fff' }}
                      activeDot={{ r: 6 }}
                      name={selectedMetric.toUpperCase()}
                    />
                  </AreaChart>
                )}
              </ResponsiveContainer>
            </Box>
          </Box>
        )}

        {showTrends && trendData.length === 0 && !loading && (
          <Flex 
            justify="center" 
            align="center" 
            h="120px" 
            bg={statBg} 
            borderRadius="xl" 
            borderWidth="1px" 
            borderColor={borderColor} 
            direction="column" 
            gap={2}
          >
            <Icon 
              as={MdShowChart} 
              boxSize={8} 
              color="gray.300" 
            />
            <Text 
              fontSize="sm" 
              color={mutedColor}
            >
              No trend data available for this period.
            </Text>
          </Flex>
        )}

      </VStack>
    );
  };

  // ── RENDER ──────────────────────────────────────────────────────────────────
  return (
    <Box
      bg={cardBg}
      borderRadius="2xl"
      boxShadow="xl"
      p={6}
      borderWidth="1px"
      borderColor={borderColor}
      position="relative"
      overflow="hidden"
      transition="all 0.3s ease"
      _hover={{ boxShadow: '2xl' }}
    >
      {/* Top gradient accent bar */}
      <Box
        position="absolute" 
        top={0} 
        left={0} 
        right={0} 
        h="4px"
        bgGradient="linear(to-r, blue.400, cyan.400, purple.400)"
      />

      {/* ── HEADER ─────────────────────────────────────────────────────────── */}
      <Flex 
        justify="space-between" 
        align="flex-start" 
        mb={4} 
        flexWrap="wrap" 
        gap={3}
      >
        <HStack spacing={3}>
          <Box 
            p={2} 
            bg={iconBg} 
            borderRadius="lg"
          >
            <Icon 
              as={MdShowChart} 
              boxSize={6} 
              color="blue.500" 
            />
          </Box>
          <VStack 
            align="flex-start" 
            spacing={0}
          >
            <Heading 
              size="md" 
              color={headingColor}
            >
              Asset Reliability Metrics
            </Heading>
            <HStack 
              spacing={2} 
              mt={0.5} 
              flexWrap="wrap"
            >
              {lastRefresh && (
                <Tooltip 
                  label={`Last updated: ${lastRefresh.toLocaleString()}`} 
                  hasArrow
                >
                  <Badge 
                    colorScheme="blue" 
                    cursor="help" 
                    fontSize="xs" 
                    borderRadius="full"
                  >
                    {Math.max(0, Math.floor((new Date() - lastRefresh) / 60000))}m ago
                  </Badge>
                </Tooltip>
              )}
              
              <Badge 
                colorScheme="purple" 
                fontSize="xs" 
                borderRadius="full"
              >
                {TIME_RANGE_LABELS[timeRange] || timeRange}
              </Badge>
              
              {refreshKey > 0 && (
                <Badge 
                  colorScheme="gray" 
                  fontSize="xs" 
                  borderRadius="full" 
                  variant="outline"
                >
                  Refresh #{refreshKey}
                </Badge>
              )}
              
              {error && (
                <Badge 
                  colorScheme="red" 
                  fontSize="xs" 
                  borderRadius="full"
                >
                  Data error
                </Badge>
              )}
            </HStack>
          </VStack>
        </HStack>

        <HStack spacing={1}>
          <Tooltip 
            label="Widget Settings" 
            hasArrow
          >
            <IconButton
              icon={<MdSettings />}
              size="sm"
              variant="ghost"
              colorScheme={isSettingsOpen ? 'blue' : 'gray'}
              onClick={onToggleSettings}
              aria-label="Settings"
              _hover={{ bg: hoverBg }}
            />
          </Tooltip>
          
          <Tooltip 
            label="Export to CSV" 
            hasArrow
          >
            <IconButton
              icon={<MdDownload />}
              size="sm"
              variant="ghost"
              onClick={handleExport}
              isDisabled={!metricsData}
              aria-label="Export data"
              _hover={{ bg: hoverBg }}
            />
          </Tooltip>
          
          <Tooltip 
            label="Toggle debug console" 
            hasArrow
          >
            <IconButton
              icon={<MdBugReport />}
              size="sm"
              variant="ghost"
              colorScheme={showDebug ? 'red' : 'gray'}
              onClick={() => setShowDebug(v => !v)}
              aria-label="Debug"
              _hover={{ bg: hoverBg }}
            />
          </Tooltip>
          
          <Tooltip 
            label="Refresh data" 
            hasArrow
          >
            <IconButton
              icon={<MdRefresh />}
              size="sm"
              variant="ghost"
              onClick={() => setRefreshKey(k => k + 1)}
              isLoading={loading}
              aria-label="Refresh"
              _hover={{ bg: hoverBg }}
            />
          </Tooltip>
        </HStack>
      </Flex>

      <Text 
        fontSize="sm" 
        color={mutedColor} 
        mb={5}
      >
        Track key reliability indicators — MTTR, MTBF, and FTFR — to optimise asset performance and maintenance strategy.
      </Text>

      {/* ── CONTENT ──────────────────────────────────────────────────────────── */}
      {renderContent()}

      {/* ── ADVANCED DETAILS ─────────────────────────────────────────────────── */}
      {!loading && metricsData && (
        <Box mt={5}>
          <Button
            size="sm"
            variant="ghost"
            colorScheme="gray"
            rightIcon={isAdvancedOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
            onClick={onToggleAdvanced}
            fontSize="xs"
          >
            Advanced Details
          </Button>
          <Collapse 
            in={isAdvancedOpen} 
            animateOpacity
          >
            <Box 
              mt={3} 
              p={4} 
              bg={statBg} 
              borderRadius="xl" 
              borderWidth="1px" 
              borderColor={borderColor} 
              fontSize="sm"
            >
              <SimpleGrid 
                columns={{ base: 1, md: 2 }} 
                spacing={4}
              >
                <Box>
                  <Text 
                    fontWeight="bold" 
                    mb={2} 
                    color={headingColor}
                  >
                    Metric Definitions
                  </Text>
                  <VStack 
                    align="flex-start" 
                    spacing={1}
                  >
                    <Code 
                      fontSize="xs" 
                      borderRadius="md" 
                      p={1}
                    >
                      MTTR — Mean Time To Repair: average hours to resolve a failure
                    </Code>
                    <Code 
                      fontSize="xs" 
                      borderRadius="md" 
                      p={1}
                    >
                      MTBF — Mean Time Between Failures: average operational hours between incidents
                    </Code>
                    <Code 
                      fontSize="xs" 
                      borderRadius="md" 
                      p={1}
                    >
                      FTFR — First Time Fix Rate: % of work orders resolved on the first visit
                    </Code>
                  </VStack>
                </Box>
                <Box>
                  <Text 
                    fontWeight="bold" 
                    mb={2} 
                    color={headingColor}
                  >
                    Session Information
                  </Text>
                  <VStack 
                    align="flex-start" 
                    spacing={1}
                  >
                    <Code 
                      fontSize="xs" 
                      borderRadius="md" 
                      p={1}
                    >
                      Time Range: {timeRange}
                    </Code>
                    <Code 
                      fontSize="xs" 
                      borderRadius="md" 
                      p={1}
                    >
                      Trend Points: {trendData.length}
                    </Code>
                    <Code 
                      fontSize="xs" 
                      borderRadius="md" 
                      p={1}
                    >
                      Last Fetch: {lastRefresh?.toLocaleString() || 'N/A'}
                    </Code>
                    <Code 
                      fontSize="xs" 
                      borderRadius="md" 
                      p={1}
                    >
                      Fetch Duration: {fetchMs ? `${fetchMs}ms` : 'N/A'}
                    </Code>
                    <Code 
                      fontSize="xs" 
                      borderRadius="md" 
                      p={1}
                    >
                      Reliability Score: {statistics?.healthScore}/100
                    </Code>
                    <Code 
                      fontSize="xs" 
                      borderRadius="md" 
                      p={1}
                    >
                      Render Count: {renderCount.current}
                    </Code>
                  </VStack>
                </Box>
              </SimpleGrid>
            </Box>
          </Collapse>
        </Box>
      )}

      {/* ── DEBUG CONSOLE ────────────────────────────────────────────────────── */}
      {renderDebugConsole()}
    </Box>
  );
};

export default ReliabilityMetricsWidget;